/**
 * Source unique des informations entreprise (NAP, horaires, zones, tarifs).
 * Utilisée par le footer, l'accueil, les pages locales et les données structurées JSON-LD.
 * Toute modification ici se répercute sur l'ensemble du site.
 */

export const SITE_URL = 'https://sos-it-services.fr';

/** Lien direct vers le formulaire d'avis Google Business Profile. */
export const GOOGLE_REVIEW_URL = 'https://g.page/r/CVVnzbhiAoZyEAE/review';

export const BUSINESS = {
  name: 'SOS IT Services',
  url: SITE_URL,
  email: 'contact@sos-it-services.fr',
  phoneDisplay: '06 26 74 82 99',
  phoneTel: '+33626748299',
  whatsappUrl: 'https://wa.me/33626748299',
  address: {
    street: '485 route de la Favorite',
    postalCode: '74580',
    city: 'Viry',
    country: 'France',
    countryCode: 'FR',
  },
  /** Géocodage Base Adresse Nationale (api-adresse.data.gouv.fr). */
  geo: { latitude: 46.109498, longitude: 6.047725 },
  hours: {
    label: '24h/24, 7j/7',
    opens: '00:00',
    closes: '23:59',
    days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
  },
  warrantyLabel: 'garantie 90 jours',
  sameAs: [
    'https://www.instagram.com/sos.it.services/',
    'https://www.tiktok.com/@sos.it.services',
    'https://www.youtube.com/@sos.it.services',
    'https://www.facebook.com/sos.it.services',
  ],
} as const;

export const FULL_ADDRESS = `${BUSINESS.address.street}, ${BUSINESS.address.postalCode} ${BUSINESS.address.city}`;

/** Recherche Google Maps sur l'adresse de l'atelier (iframe sans clé API). */
export const MAPS_EMBED_URL = `https://www.google.com/maps?q=${encodeURIComponent(
  `${BUSINESS.name}, ${FULL_ADDRESS}, France`,
)}&z=14&output=embed`;

export const MAPS_DIRECTIONS_URL = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
  `${FULL_ADDRESS}, France`,
)}`;

/** Zones d'intervention (areaServed JSON-LD + affichage). */
export const SERVICE_AREAS: string[] = [
  'Viry',
  'Saint-Julien-en-Genevois',
  'Genève',
  'Annemasse',
  'Archamps',
  'Neydens',
  'Collonges-sous-Salève',
  'Reignier-Ésery',
  'Étrembières',
  'Pays de Gex',
  'La Roche-sur-Foron',
  'Bonneville',
];

export interface PriceItem {
  id: string;
  label: string;
  /** Prix « à partir de » en euros ; null = sur devis. */
  priceFrom: number | null;
  category: 'telephone' | 'informatique' | 'web';
}

/** Tarifs « à partir de ». */
export const PRICES: PriceItem[] = [
  { id: 'batterie', label: 'Remplacement batterie', priceFrom: 60, category: 'telephone' },
  { id: 'ecran', label: 'Remplacement écran', priceFrom: 80, category: 'telephone' },
  { id: 'connecteur-nettoyage', label: 'Connecteur de charge — nettoyage', priceFrom: 30, category: 'telephone' },
  { id: 'connecteur', label: 'Connecteur de charge — remplacement', priceFrom: 90, category: 'telephone' },
  { id: 'vitre-arriere', label: 'Vitre arrière', priceFrom: 100, category: 'telephone' },
  { id: 'camera', label: 'Caméra', priceFrom: 150, category: 'telephone' },
  { id: 'lentille-camera', label: 'Lentille caméra', priceFrom: 40, category: 'telephone' },
  { id: 'depannage-pc', label: 'Dépannage PC / Mac', priceFrom: null, category: 'informatique' },
  { id: 'recuperation-donnees', label: 'Récupération de données', priceFrom: null, category: 'informatique' },
  { id: 'site-vitrine', label: 'Site vitrine', priceFrom: 350, category: 'web' },
];

export const formatPrice = (item: PriceItem): string =>
  item.priceFrom === null ? 'Sur devis' : `à partir de ${item.priceFrom} €`;

export const PHONE_PRICES = PRICES.filter((p) => p.category === 'telephone');

export const BRANDS = ['iPhone', 'Samsung', 'Xiaomi', 'iPad', 'PC', 'Mac'];

/** Pages locales « réparation téléphone » (maillage footer / pages services). */
export const PHONE_REPAIR_LOCAL_PAGES = [
  { city: 'Viry', label: 'Réparation téléphone Viry', href: '/reparation-telephone-viry' },
  { city: 'Saint-Julien-en-Genevois', label: 'Réparation téléphone Saint-Julien', href: '/reparation-telephone-saint-julien-en-genevois' },
  { city: 'Genève', label: 'Réparation iPhone Genève', href: '/reparation-iphone-geneve' },
  { city: 'Annemasse', label: 'Réparation téléphone Annemasse', href: '/reparation-telephone-annemasse' },
];
